import React, {
  useEffect, useRef, useState, useMemo, useCallback,
} from 'react';
import PropTypes from 'prop-types';

import SubpaneButton from './SubpaneButton';

import { ReactComponent as DropdownCaret } from '../../SVGAssets/DropdownCaret.svg';
import { ReactComponent as LeftRightArrow } from '../../SVGAssets/LeftRightArrow.svg';

import '../Skeleton/Skeleton.scss';

const Pane = ({
  paneData,
  activePaneId,
  subpaneComponent,
  stickyMarkup,
}) => {
  const [activeSubpaneId, setActiveSubpaneId] = useState(null);
  const [showSubpaneNavBtns, setShowSubpaneNavBtns] = useState(false);
  const [scrollerPaneWidth, setScrollerPaneWidth] = useState(null);
  const [scrollerWindowWidth, setScrollerWindowWidth] = useState(null);
  const [showSubpaneBtnsScroller, setShowSubpaneBtnsScroller] = useState(false);
  const [scrollerPaneTranslateX, setScrollerPaneTranslateX] = useState(0);
  const [leftArrowIsActive, setLeftArrowIsActive] = useState(false);
  const [rightArrowIsActive, setRightArrowIsActive] = useState(false);
  const [windowWidth, setWindowWidth] = useState(() => window.innerWidth || 0);

  // Refs for DOM elements
  const scrollerPaneRef = useRef();
  const scrollerWindowRef = useRef();
  const scrollerTriggerRef = useRef();

  // Optimize window resize handling with debouncing
  useEffect(() => {
    let timeoutId = null;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setWindowWidth(window.innerWidth || 0);
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Memoize initial subpane ID to avoid recalculation
  const initialSubpaneId = useMemo(() => (
    paneData?.length > 0 ? paneData[0]?.id : null
  ), [paneData]);

  // Set initial active subpane id when paneData changes or pane switches
  useEffect(() => {
    if (initialSubpaneId) {
      setActiveSubpaneId(initialSubpaneId);
    } else {
      setActiveSubpaneId(null);
    }
  }, [initialSubpaneId, activePaneId]);

  // Memoize active subpane data lookup to avoid repeated find operations
  const activeSubpaneData = useMemo(() => {
    if (!activeSubpaneId || !paneData?.length) {
      return null;
    }
    return paneData.find((subpaneData) => subpaneData.id === activeSubpaneId) || null;
  }, [activeSubpaneId, paneData]);

  // when window width changes, hide/show subpaneNavButtons
  useEffect(() => {
    if (windowWidth >= 768) {
      setShowSubpaneNavBtns(true);
    } else {
      setShowSubpaneNavBtns(false);
    }
  }, [windowWidth]);

  // when window width changes, get width of scrollerPane
  useEffect(() => {
    if (scrollerPaneRef.current && windowWidth >= 768) {
      // the divider is made with a 2px after element
      let rawScrollerPaneWidth = 2;
      const btns = scrollerPaneRef.current.children;

      // add up width of buttons to get width of scrollerPane
      if (btns.length > 0) {
        Array.from(btns).forEach((btn) => {
          rawScrollerPaneWidth += btn.scrollWidth;
        });
      }
      setScrollerPaneWidth(rawScrollerPaneWidth);
    } else {
      setScrollerPaneWidth(null);
    }
  }, [
    windowWidth, scrollerPaneRef, scrollerPaneRef.current, activeSubpaneId,
  ]);

  // when window width changes, get width of scrollerWindow
  // scrollerTriggerRef is the width of the parent container minus the next and previous buttons
  useEffect(() => {
    if (scrollerWindowRef.current && windowWidth >= 768) {
      const rawScrollerWindowWidth = scrollerWindowRef.current.clientWidth;
      setScrollerWindowWidth(rawScrollerWindowWidth);
    }
  }, [
    windowWidth,
    scrollerWindowRef,
    scrollerWindowRef.current,
    showSubpaneBtnsScroller,
    activeSubpaneId,
  ]);

  // use scroller if scrollerPane is wider than scrollerWindow
  // scrollerTriggerRef is the width of the parent container
  useEffect(() => {
    if (!scrollerTriggerRef.current) return;

    if (scrollerPaneWidth > scrollerTriggerRef.current.clientWidth) {
      setShowSubpaneBtnsScroller(true);
    } else {
      setShowSubpaneBtnsScroller(false);
    }
  }, [
    scrollerPaneWidth, scrollerTriggerRef.current, activeSubpaneId,
  ]);

  // make right arrow active on load or pane change if the scroller is visible
  // reset scroller pane
  useEffect(() => {
    if (showSubpaneBtnsScroller) {
      setScrollerPaneTranslateX(0);
      setRightArrowIsActive(true);
      setLeftArrowIsActive(false);
    }
  }, [showSubpaneBtnsScroller,
    activePaneId]);

  // Memoize click handlers to prevent child re-renders
  const handleToggleClick = useCallback(() => {
    setShowSubpaneNavBtns((prev) => !prev);
  }, []);

  const handlePreviousBtnClick = useCallback(() => {
    if (!scrollerPaneRef.current) return;

    // get current scrollerPane tranformX
    const rawTransform = scrollerPaneRef.current.style.transform;
    const transformValue = rawTransform.replace(/\D/g, '');
    const translateX = parseInt(transformValue, 10) || 0;

    // how much is overflowing to the left?
    const leftOverflow = translateX;

    let rawScrollerPaneTranslateX = 0;

    if (leftOverflow >= scrollerWindowWidth) {
      // rewind scrollPane same width as scrollerWindow
      rawScrollerPaneTranslateX = scrollerWindowWidth - translateX;
    } else {
      // rewind scrollPane to start
      rawScrollerPaneTranslateX = 0;
      setLeftArrowIsActive(false);
    }
    setScrollerPaneTranslateX(rawScrollerPaneTranslateX);

    // since we've shifted the content, the previous button is now legit
    setRightArrowIsActive(true);
  }, [scrollerWindowWidth]);

  const handleNextBtnClick = useCallback(() => {
    if (!scrollerPaneRef.current) return;

    // get current scrollerPane tranformX
    const rawTransform = scrollerPaneRef.current.style.transform;
    const transformValue = rawTransform.replace(/\D/g, '');
    const translateX = -(parseInt(transformValue, 10) || 0);

    // how much is overflowing to the right?
    // there's an after 2px after element we want to ignore
    const rightOverflow = (scrollerPaneWidth + translateX) - scrollerWindowWidth - 2;

    let rawScrollerPaneTranslateX = 0;
    if (rightOverflow >= scrollerWindowWidth) {
      // advance scrollPane same width as scrollerWindow
      rawScrollerPaneTranslateX = translateX - scrollerWindowWidth;
    } else {
      // advance scrollPane width of remaining overflow
      rawScrollerPaneTranslateX = translateX - rightOverflow;
      setRightArrowIsActive(false);
    }
    setScrollerPaneTranslateX(rawScrollerPaneTranslateX);

    // since we've shifted the content, the previous button is now legit
    setLeftArrowIsActive(true);
  }, [scrollerPaneWidth, scrollerWindowWidth]);

  // Memoize callback functions to prevent SubpaneButton re-renders
  // Lock scroll BEFORE state change to prevent map from causing scroll jump
  const handleSetActiveSubpaneId = useCallback((subpaneId) => {
    if (subpaneId === activeSubpaneId) return;

    const { scrollY } = window;

    // Blur any focused element to prevent focus-related scroll issues
    // (e.g., Google Maps grabbing focus and causing scroll on subpane switch)
    if (document.activeElement && document.activeElement !== document.body) {
      document.activeElement.blur();
    }

    // Lock scroll BEFORE state change - this prevents any scroll during map init
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';

    setActiveSubpaneId(subpaneId);

    // Restore after map has fully initialized (needs longer timeout)
    setTimeout(() => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
    }, 500);
  }, [activeSubpaneId]);

  const handleSetShowSubpaneNavBtns = useCallback((show) => {
    setShowSubpaneNavBtns(show);
  }, []);

  // Memoize CSS classes to prevent recalculation
  const cssClasses = useMemo(() => ({
    toggle: showSubpaneNavBtns
      ? 'subpaneButtons-toggle__button subpaneButtons-toggle__button--active'
      : 'subpaneButtons-toggle__button',
    subpaneButtons: showSubpaneBtnsScroller
      ? 'subpaneButtons subpaneButtons--scroller'
      : 'subpaneButtons',
    subpaneButtonsNav: showSubpaneBtnsScroller
      ? 'subpaneButtons-nav subpaneButtons-nav--scroller'
      : 'subpaneButtons-nav',
    subpaneButtonsPrevious: leftArrowIsActive
      ? 'subpaneButtons-previous subpaneButtons-previous--active'
      : 'subpaneButtons-previous',
    subpaneButtonsNext: rightArrowIsActive
      ? 'subpaneButtons-next subpaneButtons-next--active'
      : 'subpaneButtons-next',
  }), [
    showSubpaneNavBtns,
    showSubpaneBtnsScroller,
    leftArrowIsActive,
    rightArrowIsActive,
  ]);

  // Memoize SubpaneComponent to prevent unnecessary re-renders
  const SubpaneComponent = useMemo(() => {
    if (!activeSubpaneData) return null;

    // A subpane may carry its own component (e.g. the Resources tab appended to
    // the About pane); otherwise the pane-level component renders every subpane.
    const activeSubpaneComponent = activeSubpaneData.subpaneComponent || subpaneComponent;

    // If there's a subpaneComponent, render it directly with the data
    if (activeSubpaneComponent) {
      const ComponentType = activeSubpaneComponent.type;
      const originalProps = activeSubpaneComponent.props || {};
      // Pass uuid so detail components can fetch their own complete data
      const newProps = {
        ...originalProps,
        data: activeSubpaneData,
        uuid: activeSubpaneData.id,
        key: activeSubpaneData.id,
      };
      return <ComponentType {...newProps} />;
    }

    // Fallback: render a simple div with the subpane data if no component provided
    return (
      <div className="subpane-fallback">
        <pre>{JSON.stringify(activeSubpaneData, null, 2)}</pre>
      </div>
    );
  }, [subpaneComponent, activeSubpaneData]);

  // Memoize subpane buttons to prevent unnecessary re-renders
  const subpaneButtons = useMemo(() => {
    if (!showSubpaneNavBtns || !paneData?.length || paneData.length <= 1) return null;

    return paneData.map((subpaneData) => (
      <SubpaneButton
        key={subpaneData.id}
        subpaneDataId={subpaneData.id}
        btnText={subpaneData.subpaneButtonText || subpaneData.title}
        activeSubpaneId={activeSubpaneId}
        setActiveSubpaneId={handleSetActiveSubpaneId}
        setShowSubpaneNavBtns={handleSetShowSubpaneNavBtns}
      />
    ));
  }, [
    showSubpaneNavBtns, paneData, activeSubpaneId, handleSetActiveSubpaneId, handleSetShowSubpaneNavBtns,
  ]);

  // Memoize style object to prevent recreation
  const scrollerPaneStyle = useMemo(() => ({
    width: scrollerPaneWidth,
    transform: `translateX(${scrollerPaneTranslateX}px)`,
  }), [scrollerPaneWidth, scrollerPaneTranslateX]);

  // Check if we have skeleton data (Phase 1 only - has ID but no title/other data)
  const hasSkeletonData = paneData?.length > 0 && (paneData[0].title === null || paneData[0].title === undefined);

  if (hasSkeletonData) {
    // Show 80vh skeleton shimmer while waiting for Phase 2 data
    return (
      <div className="pane">
        <div className="subpane">
          <div className="skeleton skeleton--full" style={{ minHeight: '80vh', borderRadius: '8px' }} />
        </div>
      </div>
    );
  }

  return (
    <div className="pane">

      <div
        ref={scrollerTriggerRef}
        className={cssClasses.subpaneButtons}
      >

        {activeSubpaneData && paneData?.length > 1
          && (
            <div className="subpaneButtons-toggle">
              <div className="subpaneButtons-toggle__currentSubpaneTitle">
                {activeSubpaneData.title || activeSubpaneData.id}
              </div>
              <button
                type="button"
                className={cssClasses.toggle}
                onClick={handleToggleClick}
              >
                <DropdownCaret className="subpaneButtons-toggle__button__caret" />
              </button>
            </div>
          )}

        {showSubpaneBtnsScroller && paneData?.length > 1
          && (
            <button
              type="button"
              onClick={handlePreviousBtnClick}
              className={cssClasses.subpaneButtonsPrevious}
            >
              <LeftRightArrow className="subpaneButtons-previous__arrow" />
            </button>
          )}

        <div
          ref={scrollerWindowRef}
          className={cssClasses.subpaneButtonsNav}
        >

          <div
            ref={scrollerPaneRef}
            className="subpaneButtons-nav__scrollerPane"
            style={scrollerPaneStyle}
          >
            {subpaneButtons}
          </div>
        </div>

        {showSubpaneBtnsScroller
          && (
            <button
              type="button"
              onClick={handleNextBtnClick}
              className={cssClasses.subpaneButtonsNext}
            >
              <LeftRightArrow className="subpaneButtons-next__arrow" />
            </button>
          )}

      </div>

      <div className="subpane">
        {activeSubpaneData && SubpaneComponent}
      </div>

      {stickyMarkup}

    </div>
  );
};

// PropTypes for better type checking and performance insights
Pane.propTypes = {
  paneData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string,
      // Optional per-subpane override of the pane-level subpaneComponent.
      subpaneComponent: PropTypes.node,
    }),
  ),
  activePaneId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  subpaneComponent: PropTypes.node,
  stickyMarkup: PropTypes.node,
};

Pane.defaultProps = {
  paneData: null,
  activePaneId: null,
  subpaneComponent: null,
  stickyMarkup: null,
};

export default Pane;
